import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { AuthProvider } from "@/contexts/AuthContext";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { OwnerRoute } from "@/components/OwnerRoute";
import { AppLayout } from "@/components/AppLayout";
import { MarketingLayout } from "@/components/MarketingLayout";
import NewSearchPage from "@/pages/NewSearchPage";
import SearchJobsPage from "@/pages/SearchJobsPage";
import ResultsPage from "@/pages/ResultsPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OnboardingPage from "@/pages/OnboardingPage";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import ImpressumPage from "@/pages/ImpressumPage";
import SettingsPage from "@/pages/SettingsPage";
import MembersPage from "@/pages/MembersPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import OnboardingHelpPage from "@/pages/OnboardingHelpPage";
import CreditsPage from "@/pages/CreditsPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminTranslationsPage from "@/pages/AdminTranslationsPage";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  // CST207: the Sentry ErrorBoundary fallback strings are intentionally
  // hardcoded in German. This boundary is the last line of defence and may
  // fire before i18next has initialized (or after it has crashed), so calling
  // t() here could itself throw or return a raw key to the user. Keep these
  // strings static; any future language switch must keep a safe DE fallback.
  <Sentry.ErrorBoundary
    fallback={({ error }) => (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h1>Ein unerwarteter Fehler ist aufgetreten</h1>
        <p style={{ color: "#666", marginTop: "1rem" }}>
          Bitte laden Sie die Seite neu. Falls das Problem bestehen bleibt,
          kontaktieren Sie den Support.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            cursor: "pointer",
          }}
        >
          Seite neu laden
        </button>
      </div>
    )}
  >
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <CreditsProvider>
              <SentryRoutes>
                {/* Public marketing pages (with navbar + footer) */}
                <Route element={<MarketingLayout />}>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/impressum" element={<ImpressumPage />} />
                </Route>

                {/* Auth pages (no layout wrapper) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />

                {/* Onboarding (authenticated but no company yet — ProtectedRoute
                already skips the needsOnboarding redirect when pathname is /onboarding) */}
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <OnboardingPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/search/new" element={<NewSearchPage />} />
                  <Route path="/jobs" element={<SearchJobsPage />} />
                  <Route
                    path="/jobs/:jobId/results"
                    element={<ResultsPage />}
                  />
                  <Route path="/help" element={<OnboardingHelpPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/members" element={<MembersPage />} />
                  <Route path="/credits" element={<CreditsPage />} />
                  {/* CST202: /admin/* is platform-scoped (OWNER_USER_IDS / require_owner),
                      NOT company-scoped (role='admin'). Company-admin routes stay on
                      /settings/*. */}
                  <Route
                    path="/admin"
                    element={
                      <OwnerRoute>
                        <AdminDashboardPage />
                      </OwnerRoute>
                    }
                  />
                  <Route
                    path="/admin/translations"
                    element={
                      <OwnerRoute>
                        <AdminTranslationsPage />
                      </OwnerRoute>
                    }
                  />
                </Route>

                <Route path="*" element={<NotFound />} />
              </SentryRoutes>
            </CreditsProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
